import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api';
import type {
  CreateLegacyInput,
  LegacyRow,
  LegacyStatus,
  UpdateLegacyInput,
} from '../types';

export function useMyLegacies() {
  return useQuery({
    queryKey: ['my-legacies'],
    queryFn: () => api.myLegacies.list(),
  });
}

export function useLegacy(id: string | undefined) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: ['legacy', id],
    queryFn: () => api.myLegacies.get(id as string),
  });
}

export function useCreateLegacy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLegacyInput) => api.myLegacies.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-legacies'] }),
  });
}

export function useUpdateLegacy(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateLegacyInput) => api.myLegacies.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-legacies'] });
      qc.invalidateQueries({ queryKey: ['legacy', id] });
    },
  });
}

export function useSetLegacyStatus(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (status: LegacyStatus) => api.myLegacies.setStatus(id, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-legacies'] });
      qc.invalidateQueries({ queryKey: ['legacy', id] });
      qc.invalidateQueries({ queryKey: ['feed'] });
      qc.invalidateQueries({ queryKey: ['random'] });
    },
  });
}

export function useDeleteLegacy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.myLegacies.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-legacies'] }),
  });
}

export type { LegacyRow };